<template>
  <!-- each cell is its own small component so a conductor tick re-renders
       only the cells that actually flipped; re-rendering whole rows per
       tick was the difference between a crisp cascade and a crawl -->
  <span class="sf" :class="{ 'sf--static': reduced }" aria-hidden="true">
    <sfc_cell v-for="(cell, idx) in cells" :key="idx" :cell="cell"/>
  </span>
</template>

<script>
// a row of split-flap character cells. each cell mechanically cycles
// through the drum (one direction, with wraparound, like the real
// hardware) until it lands on its target character. four layers per
// mid-flip cell: static top = incoming char, static bottom = outgoing
// char, a falling flap (outgoing top half) and a rising flap (incoming
// bottom half) keyed on flipId so the css animation replays per step.
const DRUM = ' ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789:./-,&\''
const TICK_MS = 120
// strides adapt to distance so every spin lands within about six flips:
// long spins sample the drum coarsely, short ones flip char by char
const MIN_STRIDE = 3
const MAX_STRIDE = 8

// one shared conductor drives every flap instance on the page: a wall of
// cells costs one timer instead of hundreds, which is the difference
// between a crisp cascade and a saturated main thread
const instances = new Set()
let conductor = null
// rolling-wave counter: instances spin up in registration-order waves so
// only a moving band of cells is ever mid-flip at once
let waveSeq = 0
function ensureConductor() {
  if (conductor) return
  conductor = setInterval(() => {
    instances.forEach(component => component.step())
  }, TICK_MS)
}
function releaseConductor() {
  if (conductor && instances.size === 0) {
    clearInterval(conductor)
    conductor = null
  }
}

// one flap cell. a stateful child with a manual render function (the
// runtime-only vue build refuses inline templates): its own watcher
// subscribes to just this cell's fields, so a tick re-renders only the
// cells that flipped. the four spans are always present - mixing keyed
// and unkeyed children in one list confused vue 2's diff and left stale
// glyphs - and the flip animation replays by alternating between two
// identical keyframe names per flipId.
const SfcCell = {
  name: 'sfc_cell',
  props: { cell: { type: Object, required: true } },
  render(h) {
    const cell = this.cell
    const live = cell.current !== cell.next
    const suffix = cell.flipId % 2 ? 'b' : 'a'
    const glyph = ch => h('span', { class: 'sfc-glyph' }, ch)
    return h('span', { class: ['sfc', { 'sfc--live': live }] }, [
      h('span', { class: 'sfc-half sfc-half--top' }, [glyph(cell.next)]),
      h('span', { class: 'sfc-half sfc-half--bottom' }, [glyph(cell.current)]),
      h('span', { class: 'sfc-flap sfc-flap--top', style: { animationName: 'sfc-fall-' + suffix } }, [glyph(cell.current)]),
      h('span', { class: 'sfc-flap sfc-flap--bottom', style: { animationName: 'sfc-rise-' + suffix } }, [glyph(cell.next)]),
    ])
  },
}

export default {
  name: 'split_flap',
  components: { sfc_cell: SfcCell },
  props: {
    value: { type: String, default: '' },
    length: { type: Number, required: true },
  },
  data() {
    const reduced = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    return {
      reduced: !!reduced,
      cells: Array.from({ length: this.length }, () => ({
        current: ' ',
        next: ' ',
        target: ' ',
        hold: 0,
        flipId: 0,
      })),
    }
  },
  watch: {
    value() {
      this.applyValue()
    },
  },
  mounted() {
    this.applyValue()
  },
  beforeDestroy() {
    instances.delete(this)
    releaseConductor()
  },
  methods: {
    // uppercase, pad/truncate to length, and coerce anything outside the
    // drum to a blank flap
    normalize(value) {
      const raw = String(value || '').toUpperCase().slice(0, this.length)
      const chars = []
      for (let i = 0; i < this.length; i++) {
        const ch = raw[i] || ' '
        chars.push(DRUM.indexOf(ch) === -1 ? ' ' : ch)
      }
      return chars
    },
    applyValue() {
      const target = this.normalize(this.value)
      if (this._waveSeq === undefined) this._waveSeq = waveSeq++
      const baseHold = (this._waveSeq % 12) * 2
      let dirty = false
      this.cells.forEach((cell, i) => {
        cell.target = target[i]
        if (cell.current === cell.target) return
        if (this.reduced) {
          cell.current = cell.next = cell.target
          return
        }
        // wave offset + left-to-right stagger + jitter: the cascade reads
        // mechanical, and only a rolling band of cells is mid-flip at once
        cell.hold = baseHold + Math.round(i * 0.5 + Math.random() * 2)
        dirty = true
      })
      if (dirty) {
        instances.add(this)
        ensureConductor()
      }
    },
    // walk forward up to the adaptive stride without overshooting
    drumStep(cell) {
      const len = DRUM.length
      const distance = (DRUM.indexOf(cell.target) - DRUM.indexOf(cell.current) + len) % len
      const stride = Math.min(MAX_STRIDE, Math.max(MIN_STRIDE, Math.ceil(distance / 6)))
      let ch = cell.current
      for (let s = 0; s < stride; s++) {
        ch = DRUM[(DRUM.indexOf(ch) + 1) % len]
        if (ch === cell.target) break
      }
      cell.next = ch
    },
    step() {
      let busy = false
      this.cells.forEach(cell => {
        // commit the flap that finished falling on the previous tick
        cell.current = cell.next
        if (cell.current === cell.target) return
        busy = true
        if (cell.hold > 0) {
          cell.hold--
          return
        }
        this.drumStep(cell)
        cell.flipId++
      })
      if (!busy) {
        instances.delete(this)
        releaseConductor()
      }
    },
  },
}
</script>

<style>
/* unscoped on purpose: the cells render via a child render function,
   which never receives the parent's scope attribute. all selectors are
   sf-prefixed, so nothing can leak. cell metrics (0.66em wide, 0.06em
   gap) are mirrored by the sf-col-* width helpers in departure_board.vue;
   keep them in sync */
.sf {
  display: inline-flex;
  gap: 0.06em;
  line-height: 1;
}
.sfc {
  position: relative;
  display: inline-block;
  width: 0.66em;
  height: 1.18em;
}
/* idle cells stay completely flat: with ~800 cells on a board, keeping
   flaps mounted (let alone 3d-transformed) on every cell forces a
   compositor layer explosion that froze painting outright. flaps render
   only while a cell is mid-flip, and they animate with a scaleY squash
   rather than rotateX so no perspective layers exist at all. */
.sfc::after {
  content: '';
  position: absolute;
  left: 0; right: 0;
  top: 50%;
  height: 1px;
  margin-top: -0.5px;
  background: rgba(0, 0, 0, 0.55);
  z-index: 3;
}
.sfc-half,
.sfc-flap {
  position: absolute;
  left: 0; right: 0;
  height: 50%;
  overflow: hidden;
  background: linear-gradient(180deg, #2a2a30 0%, #232329 100%);
}
.sfc-half--top,
.sfc-flap--top { top: 0; border-radius: 0.09em 0.09em 0 0; }
.sfc-half--bottom,
.sfc-flap--bottom {
  bottom: 0;
  border-radius: 0 0 0.09em 0.09em;
  background: linear-gradient(180deg, #1e1e23 0%, #18181c 100%);
}
.sfc-glyph {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 1.18em;
  line-height: 1.18em;
  text-align: center;
  font-weight: 600;
  color: currentColor;
}
.sfc-half--bottom .sfc-glyph,
.sfc-flap--bottom .sfc-glyph { top: auto; bottom: 0; }

.sfc-flap { display: none; }
.sfc--live .sfc-flap { display: block; }
.sfc-flap--top {
  z-index: 2;
  transform-origin: 50% 100%;
  animation: sfc-fall-a 0.07s ease-in forwards;
}
.sfc-flap--bottom {
  z-index: 2;
  transform-origin: 50% 0%;
  transform: scaleY(0);
  animation: sfc-rise-a 0.07s ease-out 0.07s forwards;
}
/* a/b pairs are identical; swapping the name is what replays them */
@keyframes sfc-fall-a {
  from { transform: scaleY(1); }
  to { transform: scaleY(0); }
}
@keyframes sfc-fall-b {
  from { transform: scaleY(1); }
  to { transform: scaleY(0); }
}
@keyframes sfc-rise-a {
  from { transform: scaleY(0); }
  to { transform: scaleY(1); }
}
@keyframes sfc-rise-b {
  from { transform: scaleY(0); }
  to { transform: scaleY(1); }
}
.sf--static .sfc-flap { display: none; }
</style>
